import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/auth/callback")({
  ssr: false,
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Signing you in…");

  useEffect(() => {
    let cancelled = false;
    let unsub: (() => void) | undefined;

    const checkAndRedirect = async (userId: string, userEmail: string | null) => {
      if (cancelled) return;
      setStatus("Setting up your account…");
      // Admin bypass — never route admin through onboarding.
      if ((userEmail ?? "").toLowerCase() === "ahmity@gmail.com") {
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      try {
        const completedKey = `forexmind:onboarded:${userId}`;
        if (localStorage.getItem(completedKey) === "true") {
          navigate({ to: "/dashboard", replace: true });
          return;
        }
        const { getOnboardingStatus } = await import("@/lib/user.functions");
        const status = await getOnboardingStatus();
        if (cancelled) return;
        if (!status.needsOnboarding) localStorage.setItem(completedKey, "true");
        navigate({
          to: status.needsOnboarding ? "/onboarding" : "/dashboard",
          replace: true,
        });
      } catch {
        if (!cancelled) navigate({ to: "/onboarding", replace: true });
      }
    };

    void import("@/integrations/supabase/client").then(async ({ supabase }) => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        await checkAndRedirect(data.session.user.id, data.session.user.email ?? null);
        return;
      }

      const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (cancelled) return;
        if (event === "SIGNED_IN" && session) {
          await checkAndRedirect(session.user.id, session.user.email ?? null);
        } else if (event === "SIGNED_OUT") {
          navigate({ to: "/login", replace: true });
        }
      });
      unsub = () => sub.subscription.unsubscribe();

      setTimeout(() => {
        if (cancelled) return;
        void supabase.auth.getSession().then(({ data: d2 }) => {
          if (!d2.session && !cancelled) navigate({ to: "/login", replace: true });
        });
      }, 8000);
    });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}
